import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { SvgIconsModule } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgxContentLoadingModule } from 'ngx-content-loading';
import { LottieModule } from 'ngx-lottie';
import { SwiperModule } from 'swiper/angular';
import { AccordionsComponent } from './components/accordions/accordions.component';
import { BasicCarouselComponent } from './components/basic-carousel/basic-carousel.component';
import { CarouselItemComponent } from './components/carousel-item/carousel-item.component';
import { CarouselTitleComponent } from './components/carousel-title/carousel-title.component';
import { CarouselComponent } from './components/carousel/carousel.component';
import { CollectionCarouselComponent } from './components/collection-carousel/collection-carousel.component';
import { ContentMetaDataListComponent } from './components/content-meta-data-list/content-meta-data-list.component';
import { ContinueWatchingCarouselComponent } from './components/continue-watching-carousel/continue-watching-carousel.component';
import { CountdownButtonComponent } from './components/countdown-button/countdown-button.component';
import { CurveAnimationComponent } from './components/curve-animation/curve-animation.component';
import { DeviceModuleComponent } from './components/device-module/device-module.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { EmailCollectorAdditionalControlDirective } from './components/email-collector/email-collector-additional-control.directive';
import { EmailCollectorComponent } from './components/email-collector/email-collector.component';
import { EmailInputWithButtonComponent } from './components/email-input-with-button/email-input-with-button.component';
import { EpisodeListLayoutToggleComponent } from './components/episode-list-layout-toggle/episode-list-layout-toggle.component';
import { EpisodeListComponent } from './components/episode-list/episode-list.component';
import { FaqComponent } from './components/faq/faq.component';
import { GenericCarouselItemComponent } from './components/generic-carousel-item/generic-carousel-item.component';
import { GenericCarouselComponent } from './components/generic-carousel/generic-carousel.component';
import { GenericRedeemComponent } from './components/generic-redeem/generic-redeem.component';
import { GenreListComponent } from './components/genre-list/genre-list.component';
import { HeroItemComponent } from './components/hero-item/hero-item.component';
import { HeroComponent } from './components/hero/hero.component';
import { IconButtonComponent } from './components/icon-button/icon-button.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { MarketingActionButtonComponent } from './components/marketing-action-button/marketing-action-button.component';
import { MarketingBannerComponent } from './components/marketing-banner/marketing-banner.component';
import { LineBreakPipe } from './components/marketing-cta/line-break.pipe';
import { MarketingCtaComponent } from './components/marketing-cta/marketing-cta.component';
import { MarketingEmailBlockerComponent } from './components/marketing-email-blocker/marketing-email-blocker.component';
import { MarketingEmailCollectorComponent } from './components/marketing-email-collector/marketing-email-collector.component';
import { MarketingHeroComponent } from './components/marketing-hero/marketing-hero.component';
import { MarketingKidsComponent } from './components/marketing-kids/marketing-kids.component';
import { MarketingStudioComponent } from './components/marketing-studio/marketing-studio.component';
import { MarketingTrendingComponent } from './components/marketing-trending/marketing-trending.component';
import { MarketingUspComponent } from './components/marketing-usp/marketing-usp.component';
import { MyListButtonDirective } from './components/my-list-button/my-list-button.directive';
import { MyListCarouselComponent } from './components/my-list-carousel/my-list-carousel.component';
import { PageScreenComponent } from './components/page-screen/page-screen.component';
import { PartnerRedeemComponent } from './components/partner-redeem/partner-redeem.component';
import { PlayButtonDirective } from './components/play-button/play-button.directive';
import { PreviewOverlayGtmDirective } from './components/preview-overlay/preview-overlay-gtm.directive';
import { PreviewOverlayComponent } from './components/preview-overlay/preview-overlay.component';
import { PreviewOverlayDirective } from './components/preview-overlay/preview-overlay.directive';
import { RecommendationCarouselComponent } from './components/recommendation-carousel/recommendation-carousel.component';
import { RedeemCardComponent } from './components/redeem-card/redeem-card.component';
import { RedeemCtaTrayComponent } from './components/redeem-cta-tray/redeem-cta-tray.component';
import { RedeemInputComponent } from './components/redeem-input/redeem-input.component';
import { RedeemUspTrayComponent } from './components/redeem-usp-tray/redeem-usp-tray.component';
import { SeasonBasicCarouselComponent } from './components/season-basic-carousel/season-basic-carousel.component';
import { SeasonCarouselItemComponent } from './components/season-carousel-item/season-carousel-item.component';
import { SeasonCollectionCarouselComponent } from './components/season-collection-carousel/season-collection-carousel.component';
import { ShimmerLoadingComponent } from './components/shimmer-loading/shimmer-loading.component';
import { ShortDetailsComponent } from './components/short-details/short-details.component';
import { SpotlightTrayComponent } from './components/spotlight-tray/spotlight-tray.component';
import { StartOverButtonDirective } from './components/start-over-button/start-over-button.directive';
import { TabViewComponent } from './components/tab-view/tab-view.component';
import { TabViewTitleDirective } from './components/tab-view/tab-view.directive';
import { TopCarouselComponent } from './components/top-carousel/top-carousel.component';
import { AspectRatioModule } from './directives/aspect-ratio/aspect-ratio.module';
import { IntersectionMarkerDirective } from './directives/intersection-marker/intersection-marker.directive';
import { MarketingActionButtonDirective } from './directives/marketing-action-button/marketing-action-button.directive';
import { RerenderOnDirectionChangeDirective } from './directives/rerender-on-direction-change/rerender-on-direction-change.directive';
import { RerenderOnEventDirective } from './directives/rerender-on-event/rerender-on-event.directive';
import { UseAsPictureDirective } from './directives/use-as-picture/use-as-picture.directive';
import { OsnProgressBarModule } from './osn-progress-bar/osn-progress-bar.module';
import { SharedPipesModule } from './pipes/shared-pipes.module';
import { iconComponents } from './shared-icons';
import { EmailCollectorFreeTrialPipe } from './components/email-collector/email-collector-free-trial.pipe';

export const playerFactory = () => {
  return import('lottie-web');
};

@NgModule({
  declarations: [
    PageScreenComponent,
    CarouselComponent,
    HeroComponent,
    HeroItemComponent,
    ShortDetailsComponent,
    CarouselItemComponent,
    PreviewOverlayComponent,
    BasicCarouselComponent,
    TopCarouselComponent,
    EpisodeListComponent,
    CollectionCarouselComponent,
    ContinueWatchingCarouselComponent,
    IntersectionMarkerDirective,
    GenericCarouselComponent,
    GenericCarouselItemComponent,
    DropdownComponent,
    MyListCarouselComponent,
    CarouselTitleComponent,
    LoadingSpinnerComponent,
    CountdownButtonComponent,
    SeasonCollectionCarouselComponent,
    SeasonBasicCarouselComponent,
    SeasonCarouselItemComponent,
    MarketingCtaComponent,
    MarketingEmailCollectorComponent,
    ShimmerLoadingComponent,
    LineBreakPipe,
    RecommendationCarouselComponent,
    PreviewOverlayGtmDirective,
    MarketingKidsComponent,
    MarketingActionButtonDirective,
    CurveAnimationComponent,
    MarketingUspComponent,
    RerenderOnDirectionChangeDirective,
    MarketingStudioComponent,
    MarketingTrendingComponent,
    MarketingHeroComponent,
    UseAsPictureDirective,
    PreviewOverlayDirective,
    MarketingBannerComponent,
    GenericRedeemComponent,
    RedeemInputComponent,
    PartnerRedeemComponent,
    RedeemCtaTrayComponent,
    RedeemUspTrayComponent,
    RerenderOnEventDirective,
    FaqComponent,
    AccordionsComponent,
    GenreListComponent,
    IconButtonComponent,
    PlayButtonDirective,
    StartOverButtonDirective,
    MyListButtonDirective,
    GenreListComponent,
    TabViewComponent,
    TabViewTitleDirective,
    ContentMetaDataListComponent,
    EpisodeListLayoutToggleComponent,
    DeviceModuleComponent,
    EmailInputWithButtonComponent,
    MarketingEmailBlockerComponent,
    SpotlightTrayComponent,
    EmailCollectorComponent,
    RedeemCardComponent,
    EmailCollectorAdditionalControlDirective,
    MarketingActionButtonComponent,
    EmailCollectorFreeTrialPipe,
  ],
  imports: [
    RouterModule,
    CommonModule,
    MatTooltipModule,
    MatSelectModule,
    OsnProgressBarModule,
    SharedPipesModule,
    AspectRatioModule,
    TranslateModule,
    MatInputModule,
    MatAutocompleteModule,
    NgxContentLoadingModule,
    FormsModule,
    ReactiveFormsModule,
    SvgIconsModule.forRoot({
      sizes: {
        xs: '16px',
        sm: '20px',
        md: '24px',
        lg: '32px',
        xl: '48px',
      },
      defaultSize: 'xs',
      icons: iconComponents,
    }),
    SwiperModule,
    MatTabsModule,
    LottieModule.forRoot({ player: playerFactory }),
  ],
  exports: [
    PageScreenComponent,
    CarouselComponent,
    HeroComponent,
    MatTooltipModule,
    HeroItemComponent,
    ShortDetailsComponent,
    CarouselItemComponent,
    PreviewOverlayComponent,
    OsnProgressBarModule,
    EpisodeListLayoutToggleComponent,
    EpisodeListComponent,
    BasicCarouselComponent,
    TopCarouselComponent,
    CollectionCarouselComponent,
    ContinueWatchingCarouselComponent,
    MatInputModule,
    MatAutocompleteModule,
    GenericCarouselComponent,
    GenericCarouselItemComponent,
    DropdownComponent,
    MyListCarouselComponent,
    CountdownButtonComponent,
    CarouselTitleComponent,
    IntersectionMarkerDirective,
    LoadingSpinnerComponent,
    SharedPipesModule,
    HeroItemComponent,
    SeasonCollectionCarouselComponent,
    SeasonBasicCarouselComponent,
    SeasonCarouselItemComponent,
    NgxContentLoadingModule,
    MarketingCtaComponent,
    MarketingEmailCollectorComponent,
    ShimmerLoadingComponent,
    RecommendationCarouselComponent,
    PreviewOverlayGtmDirective,
    SvgIconsModule,
    MarketingKidsComponent,
    MarketingActionButtonDirective,
    CurveAnimationComponent,
    MarketingUspComponent,
    RerenderOnDirectionChangeDirective,
    MarketingStudioComponent,
    MarketingTrendingComponent,
    MarketingHeroComponent,
    UseAsPictureDirective,
    PreviewOverlayDirective,
    MarketingBannerComponent,
    GenericRedeemComponent,
    RedeemInputComponent,
    PartnerRedeemComponent,
    RedeemCtaTrayComponent,
    RedeemUspTrayComponent,
    RerenderOnEventDirective,
    FaqComponent,
    AccordionsComponent,
    GenreListComponent,
    IconButtonComponent,
    PlayButtonDirective,
    StartOverButtonDirective,
    MyListButtonDirective,
    GenreListComponent,
    TabViewComponent,
    TabViewTitleDirective,
    ContentMetaDataListComponent,
    DeviceModuleComponent,
    EmailInputWithButtonComponent,
    MarketingEmailBlockerComponent,
    SpotlightTrayComponent,
    EmailCollectorComponent,
    RedeemCardComponent,
    EmailCollectorAdditionalControlDirective,
    MarketingActionButtonComponent,
  ],
})
export class OsnSharedModule {}
